/**
 * Card fee preview for the take-payment screen (UX-146, DECISIONS §22).
 *
 * The amount the customer is actually charged is computed by the server
 * (backend lib/cardFees.ts) when the payment link is created, and returned
 * as `feeCents`; the payment screen shows that figure once it has it. This
 * mirror only lets staff see the number before a link exists. It must stay
 * identical to the backend formula: percent × amount + fixed, whole cents,
 * capped at 3% of the amount. A test pins the two together on shared cases.
 */

export type CardFeeSettings = {
  passToCustomer: boolean
  /** Percent, 2.9 = 2.9%. */
  percent: number
  fixedCents: number
  disclosure: string | null
}

const CAP_FRACTION = 0.03
const MAX_PERCENT = 3
const MAX_FIXED_CENTS = 100

/** Fee the customer pays on top of `amountCents` by card; 0 when not passed on. */
export function cardFeePreviewCents(amountCents: number, s: CardFeeSettings | null | undefined): number {
  if (!s?.passToCustomer || !Number.isSafeInteger(amountCents) || amountCents <= 0) return 0
  const pct = Math.min(Math.max(Number(s.percent) || 0, 0), MAX_PERCENT)
  const fixed = Math.min(Math.max(Math.round(s.fixedCents), 0), MAX_FIXED_CENTS)
  const raw = Math.round((amountCents * pct) / 100) + fixed
  return Math.max(0, Math.min(raw, Math.floor(amountCents * CAP_FRACTION)))
}

/**
 * What processing costs the business when it absorbs the fee — Stripe's
 * standard US pricing, shown as an estimate ("≈"): card 2.9% + 30¢,
 * bank (ACH) 0.8% capped at $5.
 */
export function stripeCostEstimateCents(amountCents: number): { card: number; bank: number } {
  if (!Number.isFinite(amountCents) || amountCents <= 0) return { card: 0, bank: 0 }
  return {
    card: Math.round(amountCents * 0.029) + 30,
    bank: Math.min(Math.round(amountCents * 0.008), 500),
  }
}
